import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { ProtocolValue } from '../../typings/bannersEditState';
import { AdvType } from '../../typings/domain';
import { PcodeRenderData } from '../../typings/pcode';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { Shape2, Shape2Icon } from '../Shape2/Shape2';

import './VideoPreview.css';
import * as keyset from './VideoPreview.i18n';

const i18n = i18nFactory(keyset);

export interface VideoPreviewProps {
    cls?: string;
    pcodeRenderData: PcodeRenderData;
    previewUrl?: string;
    videoPreviewUrl?: string;
    url?: string;
    isUrlValid: boolean;
    onPlay: (bannerData: PcodeRenderData, videoPreviewUrl?: string) => void;
    bannerType?: AdvType;
    protocol?: ProtocolValue;
    previewType: VideoPreviewType;
    withoutDescription?: boolean;
}

export interface VideoPreviewState {
    isPlayActive: boolean;
}

export enum VideoPreviewType {
    AUDIO = 'audio',
    VIDEO = 'video'
}

const BLOCK = 'video-preview';

export class VideoPreview extends React.Component<
    VideoPreviewProps,
    VideoPreviewState
> {
    render() {
        const { previewUrl, previewType } = this.props;

        return (
            <div
                className={Bem.cls({
                    block: BLOCK,
                    mods: { type: previewType }
                })}
            >
                {this.renderTitle()}
                {this.renderDescription()}
                {previewUrl ? this.renderFilled() : this.renderDefault()}
            </div>
        );
    }

    private renderFilled = () => {
        const {
            previewUrl,
            cls,
            url,
            isUrlValid,
            protocol = ProtocolValue.https
        } = this.props;

        return (
            <div
                className={classNames(
                    Bem.cls({
                        block: BLOCK,
                        elem: 'wrapper',
                        mods: { filled: true }
                    }),
                    cls
                )}
            >
                <div className={Bem.cls(BLOCK, 'content')}>
                    <img className={Bem.cls(BLOCK, 'image')} src={previewUrl} />

                    <div className={Bem.cls(BLOCK, 'actions')}>
                        <ButtonDecorator
                            theme={'action'}
                            view="default"
                            tone="default"
                            size={'s'}
                            text={this.getCurrentTitleForPlayBtn()}
                            onClick={this.onPlay}
                            cls={Bem.cls(BLOCK, 'button-play')}
                        />
                        <ButtonDecorator
                            theme={'normal'}
                            view="default"
                            tone="default"
                            size={'s'}
                            text={i18n('go-to-link-btn')}
                            url={protocol + url}
                            target={'_blank'}
                            type={'link'}
                            disabled={!isUrlValid || !url}
                        />
                    </div>
                </div>
            </div>
        );
    };

    private renderDefault = () => {
        const { cls } = this.props;
        const icon = this.getIcon();

        return (
            <div
                className={classNames(
                    Bem.cls({
                        block: BLOCK,
                        elem: 'wrapper',
                        mods: { empty: true }
                    }),
                    cls
                )}
            >
                <div className={Bem.cls(BLOCK, 'content')}>
                    <Shape2 icon={icon} width={55} height={55} />
                </div>
            </div>
        );
    };

    private getIcon = (): Shape2Icon => {
        const { bannerType } = this.props;

        switch (bannerType) {
            case AdvType.CPM_AUDIO:
                return Shape2Icon.noAudio;
            case AdvType.CPM_VIDEO:
            case AdvType.CPM_INDOOR:
            case AdvType.CPM_OUTDOOR:
            case AdvType.CPC_VIDEO:
                return Shape2Icon.noVideo;
            default:
                throw Error(`No icon for ${bannerType} banner`);
        }
    };

    private renderTitle() {
        const { bannerType = AdvType.CPC_VIDEO } = this.props;

        switch (bannerType) {
            case AdvType.CPC_VIDEO: {
                return (
                    <div className={Bem.cls(BLOCK, 'title')}>
                        {i18n('block-title')}
                    </div>
                );
            }

            default:
                return null;
        }
    }

    private renderDescription() {
        const {
            bannerType = AdvType.CPC_VIDEO,
            withoutDescription = false
        } = this.props;

        if (withoutDescription) {
            return null;
        }

        switch (bannerType) {
            case AdvType.CPC_VIDEO: {
                return (
                    <div className={Bem.cls(BLOCK, 'description')}>
                        {i18n('block-description')}
                    </div>
                );
            }

            default:
                return null;
        }
    }

    private getCurrentTitleForPlayBtn = () => {
        const { bannerType } = this.props;

        switch (bannerType) {
            case AdvType.CPM_AUDIO:
                return i18n('play-btn-audio');
            case AdvType.CPM_VIDEO:
            case AdvType.CPC_VIDEO:
            case AdvType.CPM_OUTDOOR:
            case AdvType.CPM_INDOOR:
                return i18n('play-btn-video');
            default:
                return i18n('play-btn-common');
        }
    };

    private onPlay = () => {
        const { onPlay, pcodeRenderData, videoPreviewUrl } = this.props;

        onPlay(pcodeRenderData, videoPreviewUrl);
    };
}
